import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import CircularProgress from '@material-ui/core/CircularProgress';
import {
    Box,
    Button,
    Card,
    CardContent,
    CardHeader,
    Chip,
    Container,
    Grid,
    IconButton,
    InputAdornment,
    Link,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@material-ui/core';
import NumberFormat from 'react-number-format';
import VisibilityIcon from '@material-ui/icons/Visibility';
import VisibilityOffIcon from '@material-ui/icons/VisibilityOff';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import copy from 'clipboard-copy';
import PublishIcon from '@material-ui/icons/Publish';
import SettingsIcon from '@material-ui/icons/Settings';
import DescriptionIcon from '@material-ui/icons/Description';
import AddToQueueIcon from '@material-ui/icons/AddToQueue';
import { useConfirm } from 'material-ui-confirm';
import { useStyles } from './css';
import { ListingPreviewer } from 'components/ListingPreviewer/ListingPreviewer';
import { AppFragment, useAppCustomPublish } from 'types/generated';
import { PageHeading } from 'components/PageHeading';
import { VendorSelector } from 'components/VendorSelector';
import { config } from '../../../config';

interface IAppDashboardView {
    app: AppFragment;
    loading: boolean;
    validationReport: any;
    validationReportLoading: boolean;
    linkToVendorLoading: boolean;
    onLinkToVendor: any;
    onTestUninstall: any;
    testUninstallLoading: boolean;
    onSubmitAppForReview: any;
    appSubmittingForReview: any;
    validationReportError: any;
    refetch: () => void;
}

export const AppDashboardView = ({
    app,
    loading,
    validationReport,
    validationReportLoading,
    linkToVendorLoading,
    onLinkToVendor,
    onTestUninstall,
    testUninstallLoading,
    onSubmitAppForReview,
    appSubmittingForReview,
    refetch,
}: IAppDashboardView) => {
    const classes = useStyles();
    const [showSecretKey, setShowSecretKey] = useState(false);
    const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
    const [publishCustomApp, { loading: publishCustomAppLoading }] = useAppCustomPublish();

    const copySecretKeyToClipboard = () => {
        copy(app.apiCredentials.secretKey);
    };
    const copyApiKeyToClipboard = () => {
        copy(app.apiCredentials.apiKey);
    };

    const [selectedVendor, setSelectedVendor] = useState(null);
    const confirm = useConfirm();
    const handleClickToLinkVendor = () => {
        confirm({
            description:
                'This will lock the app to this vendor and this cannot be changed later: ' +
                selectedVendor.name +
                ' [vendor ID: ' +
                selectedVendor.id +
                ']',
        }).then(() => {
            onLinkToVendor(selectedVendor);
        });
    };

    const publishApp = () => {
        publishCustomApp({
            variables: {
                appId: app.id,
            },
        }).finally(() => {
            refetch();
        });
    };

    const breadcrumbs = [{ label: 'Apps', link: '/extranet/apps' }];

    if (loading) {
        return <CircularProgress />;
    }
    return (
        <Container maxWidth="md">
            <PageHeading
                title={
                    <React.Fragment>
                        {app.name}{' '}
                        <Chip
                            label={app.type === 'PUBLIC' ? app.status : 'Custom app'}
                            variant="outlined"
                            size="small"
                            color="primary"
                        />
                    </React.Fragment>
                }
                breadcrumbs={breadcrumbs}
            >
                {app.listings?.length > 0 && (
                    <Button
                        variant="outlined"
                        color="primary"
                        onClick={() => {
                            setPreviewDialogOpen(true);
                        }}
                        size="small"
                    >
                        <VisibilityIcon /> &nbsp;Preview
                    </Button>
                )}
                &nbsp;
                <Button
                    variant="outlined"
                    color="primary"
                    component={RouterLink}
                    to={'/extranet/apps/' + app.id + '/edit'}
                    size="small"
                >
                    <SettingsIcon /> &nbsp;Setup
                </Button>
                &nbsp;
                <React.Fragment>
                    {app.listings.length > 0 ? (
                        <Button
                            variant="outlined"
                            color="primary"
                            component={RouterLink}
                            to={
                                '/extranet/apps/' +
                                app.id +
                                '/listings/' +
                                app.listings[0].id +
                                '/edit'
                            }
                            size="small"
                        >
                            <DescriptionIcon /> &nbsp;Listing
                        </Button>
                    ) : (
                        <Button
                            variant="contained"
                            color="primary"
                            component={RouterLink}
                            to={'/extranet/apps/' + app.id + '/listings/new'}
                            size="small"
                        >
                            + Add Listing
                        </Button>
                    )}
                </React.Fragment>
            </PageHeading>

            {app.type === 'CUSTOM' && app.linkedVendor && (
                <Card className={classes.dashboardCard} elevation={3}>
                    <CardHeader title="Status" />
                    <CardContent>
                        <Typography gutterBottom>
                            This app is linked to the following vendor:{' '}
                            <b>
                                {app.linkedVendor.name} (vendor ID: {app.linkedVendor.id})
                            </b>
                        </Typography>
                        {app.status !== 'PUBLISHED' ? (
                            <>
                                <Typography gutterBottom>
                                    The app is <Chip label="not yet published" color="secondary" />
                                </Typography>
                                <Typography gutterBottom>
                                    When you have finished setting up all the details for your app
                                    you can publish it and the vendor can perform the installation.
                                    You cannot modify app listing and pricing after publishing your
                                    custom app.
                                </Typography>
                                <Box>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        onClick={() => publishApp()}
                                    >
                                        {publishCustomAppLoading ? (
                                            <CircularProgress color={'inherit'} size={16} />
                                        ) : (
                                            'Publish your custom app'
                                        )}
                                    </Button>
                                </Box>
                            </>
                        ) : (
                            <>
                                {app.numberOfInstallations > 0 ? (
                                    <Typography gutterBottom>
                                        The app is <Chip label="installed" color="primary" />{' '}
                                        <Link
                                            to={
                                                '/extranet/apps/' +
                                                app.id +
                                                '/installations/' +
                                                app.customInstallation.id
                                            }
                                            component={RouterLink}
                                        >
                                            View details
                                        </Link>
                                    </Typography>
                                ) : (
                                    <>
                                        <Typography gutterBottom>
                                            The app is{' '}
                                            <Chip label="not yet installed" color="secondary" />
                                        </Typography>
                                        <Typography gutterBottom>
                                            Please ask the vendor to access the app store in the
                                            Bókun extranet, and install the app.
                                        </Typography>
                                    </>
                                )}
                            </>
                        )}
                    </CardContent>
                </Card>
            )}

            {app.type === 'CUSTOM' && !app.linkedVendor && (
                <Card className={classes.dashboardCard} elevation={3}>
                    <CardHeader title="Get ready for production" />
                    <CardContent>
                        <Typography gutterBottom>
                            This app has not been linked to a vendor yet. Once you link it to a
                            particular vendor, they will see the app and be able to click to install
                            it.
                        </Typography>
                        <Typography gutterBottom>
                            To link the app to a vendor, select the vendor below and click the "Link
                            to vendor" button.{' '}
                            <em>
                                Note that this will lock the app to this vendor and this cannot be
                                changed later. Hence please make sure you are selecting the correct
                                vendor.
                            </em>
                        </Typography>
                        {app.listings.length > 0 ? (
                            <Box className={classes.vendorLinkForm}>
                                <VendorSelector
                                    className={classes.vendorSelector}
                                    onChange={setSelectedVendor}
                                />{' '}
                                <Button
                                    variant="contained"
                                    color="primary"
                                    className={classes.vendorLinkButton}
                                    size="large"
                                    disabled={linkToVendorLoading || !selectedVendor}
                                    onClick={handleClickToLinkVendor}
                                >
                                    Link to vendor
                                </Button>
                            </Box>
                        ) : (
                            <Box className={classes.validationReportErrorBox}>
                                Please create a listing for your app first.
                            </Box>
                        )}
                    </CardContent>
                </Card>
            )}

            <Card className={classes.dashboardCard} elevation={3}>
                <CardHeader title="Overview" />
                <CardContent>
                    {validationReportLoading && <CircularProgress />}
                    {validationReport && (
                        <div>
                            {validationReport.valid ? (
                                <Box className={classes.validationReportSuccessBox}>
                                    <Typography>Well done! No issues were found.</Typography>
                                </Box>
                            ) : (
                                <Box className={classes.validationReportErrorBox}>
                                    <Typography
                                        variant="subtitle1"
                                        className={classes.validationReportErrorHeading}
                                    >
                                        Please fix the following issues
                                    </Typography>
                                    {validationReport.items.map((item, index) => (
                                        <ul key={index}>
                                            <li>{item.message}</li>
                                        </ul>
                                    ))}
                                    {validationReport.items.some(
                                        l => !l.valid && l.type === 'TERM_AGREEMENT'
                                    ) && (
                                        <Box mb={2}>
                                            <Typography variant="subtitle2">
                                                <Link
                                                    to={'/extranet/account'}
                                                    component={RouterLink}
                                                >
                                                    View terms in Account settings
                                                </Link>
                                            </Typography>
                                        </Box>
                                    )}
                                    {validationReport.listingsReports
                                        .filter(l => !l.valid)
                                        .map((listingReport, index) => (
                                            <div key={index}>
                                                <Typography variant="subtitle2">
                                                    <Link
                                                        to={
                                                            '/extranet/apps/' +
                                                            app.id +
                                                            '/listings/' +
                                                            listingReport.listingId +
                                                            '/edit'
                                                        }
                                                        component={RouterLink}
                                                    >
                                                        Listing (English)
                                                    </Link>
                                                    :
                                                </Typography>
                                                <ul>
                                                    {listingReport.items.map((item, i) => (
                                                        <li key={i}>{item.message}</li>
                                                    ))}
                                                </ul>
                                            </div>
                                        ))}
                                </Box>
                            )}
                        </div>
                    )}

                    {app.type === 'PUBLIC' &&
                        (app.status === 'DRAFT' || app.status === 'REJECTED') && (
                            <Typography gutterBottom className={classes.cardSection}>
                                <Typography variant="subtitle1" gutterBottom>
                                    Test installation
                                </Typography>
                                {!app.testInstallation ? (
                                    <Button
                                        variant="outlined"
                                        href={config.baseUrl + '/appstore/test/' + app.id}
                                    >
                                        <AddToQueueIcon /> &nbsp;Test installation now
                                    </Button>
                                ) : (
                                    <>
                                        <Button
                                            variant="outlined"
                                            target="_blank"
                                            href={config.baseUrl + '/appstore/test/' + app.id}
                                        >
                                            Open app
                                        </Button>{' '}
                                        <Button
                                            variant="outlined"
                                            href={'/api/graphiql/' + app.testInstallation.id}
                                        >
                                            Open GraphiQL
                                        </Button>{' '}
                                        <Button
                                            onClick={() => onTestUninstall()}
                                            disabled={testUninstallLoading}
                                            variant="outlined"
                                        >
                                            Remove test installation
                                        </Button>
                                    </>
                                )}
                            </Typography>
                        )}

                    {app.type === 'PUBLIC' &&
                        (app.status === 'DRAFT' || app.status === 'REJECTED') && (
                            <Button
                                disabled={
                                    appSubmittingForReview ||
                                    (validationReport &&
                                        !validationReport.valid &&
                                        app.status !== 'REJECTED')
                                }
                                onClick={() => onSubmitAppForReview()}
                                variant="outlined"
                                color="primary"
                            >
                                {appSubmittingForReview ? (
                                    <CircularProgress size={24} />
                                ) : (
                                    <>
                                        <PublishIcon /> &nbsp;Submit for review
                                    </>
                                )}
                            </Button>
                        )}

                    {app.type === 'PUBLIC' && app.status === 'PUBLISHED' && (
                        <div>
                            <Typography>
                                This app has been installed {app.numberOfInstallations} time(s).
                            </Typography>
                            {app.numberOfInstallations > 0 && (
                                <Link
                                    to={'/extranet/apps/' + app.id + '/installations'}
                                    component={RouterLink}
                                >
                                    View installations
                                </Link>
                            )}
                        </div>
                    )}
                </CardContent>
            </Card>

            <ListingPreviewer
                listing={app.listings[0]}
                previewDialogOpen={previewDialogOpen}
                setPreviewDialogOpen={setPreviewDialogOpen}
            />
            {app.chargeType === 'RECURRING' && (
                <Card className={classes.dashboardCard} elevation={3}>
                    <CardHeader
                        title="Recurring Price Plans"
                        action={
                            <Button
                                size="small"
                                color="primary"
                                variant={'contained'}
                                component={RouterLink}
                                disabled={app.status === 'PUBLISHED' || app.status === 'IN_REVIEW'}
                                to={'/extranet/apps/' + app.id + '/priceplans/new'}
                            >
                                + Add price plan
                            </Button>
                        }
                    />
                    <CardContent>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Name</TableCell>
                                    <TableCell>Type</TableCell>
                                    <TableCell>Price</TableCell>
                                    <TableCell />
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {app.pricePlans.map((plan, index) => (
                                    <TableRow key={index}>
                                        <TableCell>{plan.name}</TableCell>
                                        <TableCell>{plan.type}</TableCell>
                                        <TableCell>
                                            {plan.type === 'MONTHLY_CHARGE' && (
                                                <React.Fragment>
                                                    <NumberFormat
                                                        value={plan.monthlyAmount}
                                                        displayType={'text'}
                                                        thousandSeparator={true}
                                                        prefix={'$'}
                                                    />
                                                    &nbsp;per month
                                                    {plan.discountedYearlyPrice && (
                                                        <React.Fragment>
                                                            {' '}
                                                            /{' '}
                                                            <NumberFormat
                                                                value={plan.yearlyAmount}
                                                                displayType={'text'}
                                                                thousandSeparator={true}
                                                                prefix={'$'}
                                                            />{' '}
                                                            per year{' '}
                                                        </React.Fragment>
                                                    )}
                                                </React.Fragment>
                                            )}
                                            {plan.type === 'YEARLY_CHARGE' && (
                                                <React.Fragment>
                                                    <NumberFormat
                                                        value={plan.yearlyAmount}
                                                        displayType={'text'}
                                                        thousandSeparator={true}
                                                        prefix={'$'}
                                                    />{' '}
                                                    per year
                                                </React.Fragment>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {!(
                                                app.status === 'PUBLISHED' ||
                                                app.status === 'IN_REVIEW'
                                            ) && (
                                                <Button
                                                    to={
                                                        '/extranet/apps/' +
                                                        app.id +
                                                        '/priceplans/' +
                                                        plan.id +
                                                        '/edit'
                                                    }
                                                    variant="outlined"
                                                    size="small"
                                                    component={RouterLink}
                                                >
                                                    Open
                                                </Button>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </CardContent>
                </Card>
            )}

            <Card className={classes.dashboardCard} elevation={3}>
                <CardHeader title="App Credentials" />
                <CardContent>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <TextField
                                variant="outlined"
                                fullWidth
                                disabled
                                label="API key"
                                value={app.apiCredentials.apiKey}
                                InputProps={{
                                    endAdornment: (
                                        <React.Fragment>
                                            <InputAdornment position="end">
                                                <IconButton onClick={copyApiKeyToClipboard}>
                                                    <FileCopyIcon />
                                                </IconButton>
                                            </InputAdornment>
                                        </React.Fragment>
                                    ),
                                }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                variant="outlined"
                                fullWidth
                                disabled
                                label="Secret key"
                                type={showSecretKey ? 'text' : 'password'}
                                value={app.apiCredentials.secretKey}
                                InputProps={{
                                    endAdornment: (
                                        <React.Fragment>
                                            <InputAdornment position="end">
                                                <IconButton
                                                    onClick={() => setShowSecretKey(!showSecretKey)}
                                                >
                                                    {showSecretKey ? (
                                                        <VisibilityIcon />
                                                    ) : (
                                                        <VisibilityOffIcon />
                                                    )}
                                                </IconButton>
                                            </InputAdornment>
                                            <InputAdornment position="end">
                                                <IconButton onClick={copySecretKeyToClipboard}>
                                                    <FileCopyIcon />
                                                </IconButton>
                                            </InputAdornment>
                                        </React.Fragment>
                                    ),
                                }}
                            />
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            <Card className={classes.dashboardCard} elevation={3}>
                <CardHeader
                    title="Webhooks"
                    action={
                        <Button
                            size="small"
                            color="primary"
                            variant={'contained'}
                            component={RouterLink}
                            to={'/extranet/apps/' + app.id + '/webhooks/new'}
                        >
                            + Add webhook
                        </Button>
                    }
                />
                <CardContent>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Topic</TableCell>
                                <TableCell>URL</TableCell>
                                <TableCell />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {app.webhookSubscriptions.map(subscription => (
                                <TableRow key={subscription.id}>
                                    <TableCell>{subscription.topic}</TableCell>
                                    <TableCell>{subscription.url}</TableCell>
                                    <TableCell>
                                        <Button
                                            to={
                                                '/extranet/apps/' +
                                                app.id +
                                                '/webhooks/' +
                                                subscription.id +
                                                '/edit'
                                            }
                                            variant="outlined"
                                            size="small"
                                            component={RouterLink}
                                        >
                                            Open
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>
        </Container>
    );
};
